import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import ClientReport from "./ClientReport";
import { PresetButtons, buildPresets } from "./PeriodPresets";

const AGENCY_LOGO = "https://customer-assets.emergentagent.com/job_6f75df89-405a-4cf8-ab5f-a582c321d0a0/artifacts/ysc8kerb_AFA%20transparent.png";

export default function PublicReport({ API }) {
  const { token } = useParams();
  const [client, setClient] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [adMetrics, setAdMetrics] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [presets, setPresets] = useState([]);
  const [activePreset, setActivePreset] = useState(null);

  const selectPreset = (p) => {
    setActivePreset(p.key);
    setClient((prev) => ({ ...prev, report_start: p.start, report_end: p.end }));
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await axios.get(`${API}/public/report/${token}`);
        const clientData = res.data.client;
        const [metricsRes, adMetricsRes, coverageRes] = await Promise.all([
          axios.get(`${API}/metrics?client_id=${clientData.id}`).catch(() => ({ data: [] })),
          axios.get(`${API}/ad-metrics?client_id=${clientData.id}`).catch(() => ({ data: [] })),
          axios.get(`${API}/data-coverage?client_id=${clientData.id}`).catch(() => ({ data: { months: [] } })),
        ]);
        const built = buildPresets(coverageRes.data?.months);
        setPresets(built);
        if (built.length) {
          setActivePreset(built[0].key);
          setClient({ ...clientData, report_start: built[0].start, report_end: built[0].end });
        } else {
          setClient(clientData);
        }
        setMetrics(metricsRes.data);
        setAdMetrics(adMetricsRes.data);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) setError("This report was not found.");
        else if (status === 403) setError("This report link has been deactivated.");
        else if (status === 410) setError("This report link has expired.");
        else setError("Unable to load report.");
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [API, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <img src={AGENCY_LOGO} alt="Agency" className="h-16 mx-auto mb-4 animate-pulse" />
          <p className="text-slate-500 text-sm">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <img src={AGENCY_LOGO} alt="Agency" className="h-16 mx-auto mb-6" />
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Report Unavailable</h1>
            <p className="text-slate-500">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-report">
      <Toaster position="top-right" richColors />
      {/* Agency branded header — on-brand dark teal, matches the report canvas */}
      <header className="bg-[#0a3d54] border-b border-lime-400/30 sticky top-0 z-50 shadow-lg shadow-slate-900/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-3">
          {/* Agency logo — subtle white tint so it reads on dark teal */}
          <img src={AGENCY_LOGO} alt="Agency" className="h-8 md:h-10" />
          <div className="text-right min-w-0">
            <p className="text-lime-300 text-[11px] md:text-xs uppercase tracking-[0.25em] font-bold">Performance Report</p>
            <p className="text-white text-xs md:text-sm font-semibold truncate">{client?.name}</p>
          </div>
        </div>
      </header>

      {/* Report content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <ClientReport
          client={client}
          metrics={metrics}
          adMetrics={adMetrics}
          onBack={() => {}}
          onRefresh={() => {}}
          onViewChange={() => {}}
          API={API}
          readOnly={true}
          hideDatePill={presets.length > 0}
          periodControl={presets.length > 0 ? (
            <PresetButtons presets={presets} active={activePreset} onSelect={selectPreset} tone="onDark" />
          ) : null}
        />
      </main>

      {/* Agency footer — on-brand */}
      <footer className="bg-[#0a3d54] border-t border-lime-400/20 py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
          <img src={AGENCY_LOGO} alt="Agency" className="h-7 md:h-8 opacity-70" />
          <p className="text-cyan-200/60 text-[11px] md:text-xs">Confidential. Prepared exclusively for {client?.name}</p>
        </div>
      </footer>
    </div>
  );
}
